// Access to the Laravel backend, abstracted away from origin-relative URLs.
//
// The web app reaches Laravel with bare `fetch('/backend/...')` calls, which
// resolve only because Kong fronts the web app and the admin app on one origin.
// React Native has no document origin, so a relative path means nothing there --
// this is the single hardest coupling to break in the core code.

export interface BackendClient {
  /**
   * Performs a request against the backend.
   *
   * `path` is origin-relative and must start with a slash (e.g.
   * `/backend/memberships/123/reconcile-todos`); the implementation supplies the
   * base URL. Rejections and non-2xx responses are the caller's to handle, and
   * the returned Response is not inspected here -- callers already branch on
   * `resp.ok` themselves.
   *
   * Authorization is deliberately NOT handled here. The callers that need a
   * bearer token already hold a Supabase client and resolve the token
   * themselves, including the "no token yet, skip the call entirely" branch that
   * has to stay observable to them.
   */
  fetch(path: string, init?: RequestInit): Promise<Response>
}

/**
 * Joins a base URL and an origin-relative path without doubling or dropping the
 * separating slash. Shared by the web and native implementations so the two
 * cannot disagree about it.
 */
export function joinBackendUrl(baseUrl: string, path: string): string {
  if (!baseUrl) {
    return path
  }
  const trimmedBase = baseUrl.replace(/\/+$/, '')
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  return `${trimmedBase}${normalizedPath}`
}
